/* client/src/services/apiService.ts */
const API_BASE_URL = 'https://api.koodo.tech/api';

// Helper function to clean data by converting undefined to null
const cleanData = (data: any): any => {
  if (data === null || data === undefined) {
    return data;
  }
  
  if (Array.isArray(data)) {
    return data.map(item => cleanData(item));
  }
  
  if (typeof data === 'object') {
    const cleaned: any = {};
    for (const key in data) {
      if (data.hasOwnProperty(key)) {
        cleaned[key] = data[key] === undefined ? null : cleanData(data[key]);
      }
    }
    return cleaned;
  }
  
  return data;
};

// Helper function for API calls
const apiRequest = async (endpoint: string, options: RequestInit = {}) => {
  const defaultOptions: RequestInit = {
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
    credentials: 'include', // ← CRITICAL: Include cookies
  };

  // Clean the request body if it exists
  let cleanedBody = options.body;
  if (options.body && typeof options.body === 'string') {
    try {
      const parsedBody = JSON.parse(options.body);
      const cleanedData = cleanData(parsedBody);
      cleanedBody = JSON.stringify(cleanedData);
    } catch (error) {
      console.warn('Could not parse request body for cleaning:', error);
    }
  }

  console.log(`API Request: ${endpoint}`, { 
    method: options.method, 
    body: cleanedBody,
    headers: defaultOptions.headers,
    credentials: 'include' // This should show in logs
  });

  try {
    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      ...defaultOptions,
      ...options,
      body: cleanedBody ? cleanedBody : undefined,
      credentials: 'include',
    });
    
    console.log(`API Response: ${endpoint}`, response.status, response.statusText);
    
    // Handle unauthorized responses
    if (response.status === 401 || response.status === 403) {
      const errorText = await response.text();
      let errorMessage = `Authentication failed`;
      
      try {
        const errorData = JSON.parse(errorText);
        errorMessage = errorData.error || errorMessage;
      } catch {
        errorMessage = errorText || errorMessage;
      }
      
      // Clear user data and redirect to login
      localStorage.removeItem('user');
      window.location.href = '/login';
      throw new Error(errorMessage);
    }
    
    if (!response.ok) {
      const errorText = await response.text();
      let errorMessage = `API error: ${response.status} ${response.statusText}`;
      
      try {
        const errorData = JSON.parse(errorText);
        errorMessage = errorData.error || errorMessage;
        console.error('API error response details:', errorData);
        if (errorData.details) {
          console.error('Validation errors:', errorData.details);
        }
      } catch {
        errorMessage = errorText || errorMessage;
        console.error('API error response text:', errorText);
      }
      
      console.error('API error details:', errorMessage);
      throw new Error(errorMessage);
    }
    
    // Handle empty responses
    const contentType = response.headers.get('content-type');
    if (contentType && contentType.includes('application/json')) {
      const data = await response.json();
      console.log(`API Success: ${endpoint}`, data);
      return data;
    } else {
      console.log(`API Success: ${endpoint} (no content)`);
      return { message: 'Success' };
    }
  } catch (error) {
    console.error(`API Error for ${endpoint}:`, error);
    throw error;
  }
};



// Auth API calls - UPDATED FOR COOKIE FALLBACK
export const apiLogin = (email: string, password: string) => {
  return apiRequest('/auth/login', {
    method: 'POST',
    body: JSON.stringify({ email, password }),
  }).then(response => {
    // Store both cookie (automatic) and token (fallback)
    if (response.token && response.user) {
      localStorage.setItem('token', response.token); // Fallback
      localStorage.setItem('user', JSON.stringify(response.user));
    }
    // Return the complete response object that includes both user and token
    return response;
  });
};


// Remove token storage from other auth functions
export const apiRegister = (name: string, email: string, password: string) => {
  return apiRequest('/auth/register', {
    method: 'POST',
    body: JSON.stringify({ name, email, password }),
  });
};

// Logout function - UPDATED
export const apiLogout = () => {
  return apiRequest('/auth/logout', {
    method: 'POST',
  }).then(() => {
    localStorage.removeItem('user');
    localStorage.removeItem('token');
    return Promise.resolve();
  });
};

// Add verify token function
export const apiVerifyToken = () => {
  return apiRequest('/auth/verify');
};

// Dashboard API calls
export const apiGetDashboard = () => {
  return apiRequest('/dashboard');
};

// Transactions API calls
export const apiGetTransactions = (businessUnitId?: number) => {
  const params = businessUnitId ? `?business_unit_id=${businessUnitId}` : '';
  return apiRequest(`/transactions${params}`);
};

export const apiAddTransaction = (transactionData: any) => {
  return apiRequest('/transactions', {
    method: 'POST',
    body: JSON.stringify(transactionData), // REMOVE the {transaction: } wrapper
  });
};

export const apiUpdateTransaction = (id: number, transactionData: any) => {
  return apiRequest(`/transactions/${id}`, {
    method: 'PUT',
    body: JSON.stringify(transactionData), // REMOVE the {transaction: } wrapper
  });
};

export const apiDeleteTransaction = (id: number) => {
  return apiRequest(`/transactions/${id}`, {
    method: 'DELETE',
  });
};

// Financial Reports API calls
export const apiGetFinancialReports = (dateRange: { start: string; end: string; period: string }) => {
  const params = new URLSearchParams();
  params.append('start_date', dateRange.start);
  params.append('end_date', dateRange.end);
  params.append('period', dateRange.period);
  
  return apiRequest(`/financial-reports?${params.toString()}`);
};

// Expenses API calls
export const apiGetExpenses = (businessUnitId?: number) => {
  const params = businessUnitId ? `?business_unit_id=${businessUnitId}` : '';
  return apiRequest(`/expenses${params}`);
};

export const apiAddExpense = (expenseData: any) => {
  return apiRequest('/expenses', {
    method: 'POST',
    body: JSON.stringify(expenseData),
  });
};

export const apiGetTotalInvestmentAll = async (): Promise<any> => {
  return apiRequest('/investments/total-all');
};

export const apiUpdateExpense = (id: number, expenseData: any) => {
  return apiRequest(`/expenses/${id}`, {
    method: 'PUT',
    body: JSON.stringify(expenseData),
  });
};

export const apiDeleteExpense = (id: number) => {
  return apiRequest(`/expenses/${id}`, {
    method: 'DELETE',
  });
};

// Business Units API calls
export const apiGetBusinessUnits = () => {
  return apiRequest('/business-units');
};

// ADD THIS FUNCTION - Get ALL business units including those without inventory
export const apiGetAllBusinessUnits = () => {
  return apiRequest('/business-units/all');
};


// Materials API calls
export const apiGetMaterials = () => {
  return apiRequest('/materials');
};

export const apiAddMaterial = (materialData: any) => {
  return apiRequest('/materials', {
    method: 'POST',
    body: JSON.stringify(materialData),
  });
};

export const apiUpdateMaterial = (id: number, materialData: any) => {
  return apiRequest(`/materials/${id}`, {
    method: 'PUT',
    body: JSON.stringify(materialData),
  });
};

export const apiDeleteMaterial = (id: number) => {
  return apiRequest(`/materials/${id}`, {
    method: 'DELETE',
  });
};

// Investments API calls
export const apiGetInvestments = (businessUnitId?: number) => {
  const params = businessUnitId ? `?business_unit_id=${businessUnitId}` : '';
  return apiRequest(`/investments${params}`);
};

// Add export keyword to this function
export const apiGetTotalInvestment = async (): Promise<number> => {
  const response = await apiRequest('/investments/total');
  return response.total_investment;
};

export const apiGetInvestmentReturns = (investmentId?: number) => {
  const params = investmentId ? `?investment_id=${investmentId}` : '';
  return apiRequest(`/investments/returns${params}`);
};

export const apiAddInvestment = (investmentData: any) => {
  return apiRequest('/investments', {
    method: 'POST',
    body: JSON.stringify(investmentData),
  });
};

export const apiAddInvestmentReturn = (returnData: any) => {
  return apiRequest('/investments/returns', {
    method: 'POST',
    body: JSON.stringify(returnData),
  });
};

export const apiUpdateInvestment = (id: number, investmentData: any) => {
  return apiRequest(`/investments/${id}`, {
    method: 'PUT',
    body: JSON.stringify(investmentData),
  });
};

export const apiDeleteInvestment = (id: number) => {
  return apiRequest(`/investments/${id}`, {
    method: 'DELETE',
  });
};

// Inventory functions
export const apiGetInventory = (filters: any = {}) => {
  const queryParams = new URLSearchParams();
  Object.keys(filters).forEach(key => {
    if (filters[key]) {
      queryParams.append(key, filters[key]);
    }
  });
  const queryString = queryParams.toString();
  return apiRequest(`/inventory${queryString ? `?${queryString}` : ''}`);
};

export const apiGetInventoryItem = (id: number) => 
  apiRequest(`/inventory/${id}`);

export const apiAddInventoryItem = (data: any) => 
  apiRequest('/inventory', {
    method: 'POST',
    body: JSON.stringify(data),
  });

export const apiUpdateInventoryItem = (id: number, data: any) => 
  apiRequest(`/inventory/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });

export const apiDeleteInventoryItem = (id: number) => 
  apiRequest(`/inventory/${id}`, {
    method: 'DELETE',
  });

export const apiGetInventoryLocations = () => 
  apiRequest('/inventory/locations/all');

export const apiCreateInventoryLocation = (locationData: any) => {
  return apiRequest('/inventory/locations', {
    method: 'POST',
    body: JSON.stringify(locationData),
  });
};

export const apiGetInvestmentTotalsByBusinessUnit = async (): Promise<any> => {
  return apiRequest('/investments/totals-by-business-unit');
};

export const apiGetInventoryTransfers = () => 
  apiRequest('/inventory/transfers/all');

export const apiCreateInventoryTransfer = (data: any) => 
  apiRequest('/inventory/transfers', {
    method: 'POST',
    body: JSON.stringify(data),
  });

export const apiUpdateTransferStatus = (id: number, status: string) => 
  apiRequest(`/inventory/transfers/${id}/status`, {
    method: 'PUT',
    body: JSON.stringify({ status }),
  });

export const apiGetInventorySummary = () => 
  apiRequest('/inventory/summary');

export const apiUpdateInventoryItemStatus = (id: number, status: string) => 
  apiRequest(`/inventory/${id}/status`, {
    method: 'PUT',
    body: JSON.stringify({ status }),
  });

export const apiUpdateInventoryItemLocation = (itemId: number, locationId: number) => {
  return apiRequest(`/inventory/items/${itemId}/location`, {
    method: 'PUT',
    body: JSON.stringify({ location_id: locationId }),
  });
};

export const apiGetMetalRates = async (): Promise<any> => {
  try {
    const response = await fetch('/api/metal-rates');
    if (!response.ok) {
      throw new Error('Failed to fetch metal rates');
    }
    return await response.json();
  } catch (error) {
    console.error('Error fetching metal rates:', error);
    throw error;
  }
};

// Admin verification
export const apiVerifyAdmin = () => {
  return apiRequest('/admin/verify-admin');
};

// Business Units CRUD operations - ADD THESE FUNCTIONS
export const apiAddBusinessUnit = (data: any) => {
  return apiRequest('/business-units', {
    method: 'POST',
    body: JSON.stringify(data),
  });
};

export const apiUpdateBusinessUnit = (id: number, data: any) => {
  return apiRequest(`/business-units/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
};

export const apiDeleteBusinessUnit = (id: number) => {
  return apiRequest(`/business-units/${id}`, {
    method: 'DELETE',
  });
};

// Get partnership shares for a business unit
export const apiGetPartnershipShares = async (businessUnitId: number): Promise<{
  business_unit_name: string;
  percentage: number;
}> => {
  try {
    const response = await apiRequest(`/profit/partnership-shares/${businessUnitId}`);
    return response;
  } catch (error) {
    console.error('Error fetching partnership shares:', error);
    // Fallback to default percentages based on business unit
    const defaultPercentage = businessUnitId === 1 ? 33 : 16;
    const defaultName = `Unit ${String.fromCharCode(64 + businessUnitId)}`;
    
    return {
      business_unit_name: defaultName,
      percentage: defaultPercentage
    };
  }
};

// Get total booked profit for a business unit
export const apiGetBookedProfitTotal = async (businessUnitId: number): Promise<{
  total_booked: number;
}> => {
  try {
    const response = await apiRequest(`/profit/booked-total/${businessUnitId}`);
    return response;
  } catch (error) {
    console.error('Error fetching booked profit total:', error);
    return { total_booked: 0 };
  }
};

// Get profit bookings
export const apiGetProfitBookings = async (businessUnitId?: number): Promise<any[]> => {
  try {
    const endpoint = businessUnitId 
      ? `/profit/bookings?business_unit_id=${businessUnitId}`
      : '/profit/bookings';
    const response = await apiRequest(endpoint);
    return response;
  } catch (error) {
    console.error('Error fetching profit bookings:', error);
    return [];
  }
};

// Get profit withdrawals
export const apiGetProfitWithdrawals = async (): Promise<any[]> => {
  try {
    const response = await apiRequest('/profit/withdrawals');
    return response;
  } catch (error) {
    console.error('Error fetching profit withdrawals:', error);
    return [];
  }
};

// Book profit
export const apiBookProfit = async (bookingData: {
  business_unit_id: string;
  booking_date: string;
  amount: string;
  description: string;
  gold_rate: number;
  inventory_value: number;
}): Promise<any> => {
  try {
    const response = await apiRequest('/profit/book', {
      method: 'POST',
      body: JSON.stringify(bookingData),
    });
    return response;
  } catch (error) {
    console.error('Error booking profit:', error);
    throw error;
  }
};

// Withdraw profit
export const apiWithdrawProfit = async (withdrawalData: {
  profit_booking_id: string;
  withdrawal_date: string;
  amount: string;
  description: string;
}): Promise<any> => {
  try {
    const response = await apiRequest('/profit/withdraw', {
      method: 'POST',
      body: JSON.stringify(withdrawalData),
    });
    return response;
  } catch (error) {
    console.error('Error withdrawing profit:', error);
    throw error;
  }
};

// Get profit summary
export const apiGetProfitSummary = async (): Promise<any[]> => {
  try {
    const response = await apiRequest('/profit/summary');
    return response;
  } catch (error) {
    console.error('Error fetching profit summary:', error);
    return [];
  }
};
// Business Unit Types
export const apiGetBusinessUnitTypes = () => {
  return apiRequest('/admin/business-unit-types');
};

export const apiAddBusinessUnitType = (data: any) => {
  return apiRequest('/admin/business-unit-types', {
    method: 'POST',
    body: JSON.stringify(data),
  });
};

export const apiUpdateBusinessUnitType = (id: number, data: any) => {
  return apiRequest(`/admin/business-unit-types/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
};

export const apiDeleteBusinessUnitType = (id: number) => {
  return apiRequest(`/admin/business-unit-types/${id}`, {
    method: 'DELETE',
  });
};

// Material Types
export const apiGetMaterialTypes = () => {
  return apiRequest('/admin/material-types');
};

export const apiAddMaterialType = (data: any) => {
  return apiRequest('/admin/material-types', {
    method: 'POST',
    body: JSON.stringify(data),
  });
};

export const apiUpdateMaterialType = (id: number, data: any) => {
  return apiRequest(`/admin/material-types/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
};

export const apiDeleteMaterialType = (id: number) => {
  return apiRequest(`/admin/material-types/${id}`, {
    method: 'DELETE',
  });
};

// Expense Categories
export const apiGetExpenseCategories = () => {
  return apiRequest('/expense-categories');
};

// Keep the admin version if needed elsewhere:
export const apiGetAdminExpenseCategories = () => {
  return apiRequest('/admin/expense-categories');
};

export const apiAddExpenseCategory = (data: any) => {
  return apiRequest('/admin/expense-categories', {
    method: 'POST',
    body: JSON.stringify(data),
  });
};

export const apiUpdateExpenseCategory = (id: number, data: any) => {
  return apiRequest(`/admin/expense-categories/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
};

export const apiDeleteExpenseCategory = (id: number) => {
  return apiRequest(`/admin/expense-categories/${id}`, {
    method: 'DELETE',
  });
};

// In your apiService.ts file, GetTotalInventory
export const apiGetTotalInventory = async (): Promise<{
  total_weight: number;
  total_value: number;
  business_units: Array<{
    unit: string;
    quantity: number;
    value: number;
    average_rate: number;
  }>;
}> => {
  try {
    console.log('🔍 Fetching inventory total from:', `${API_BASE_URL}/inventory/total`);
    
    const response = await fetch(`${API_BASE_URL}/inventory/total`, {
      headers: {
        'Content-Type': 'application/json'
      },
      credentials: 'include' // ← ADD THIS FOR COOKIES
    });
    
    console.log('🔍 Inventory total response status:', response.status, response.statusText);
    
    if (!response.ok) {
      const errorText = await response.text();
      console.error('❌ Inventory total API error:', errorText);
      throw new Error(`HTTP error! status: ${response.status}, message: ${errorText}`);
    }
    
    const data = await response.json();
    console.log('✅ Inventory total API success response:', data);
    
    return data;
  } catch (error) {
    console.error('❌ Error fetching total inventory:', error);
    throw error;
  }
};

// Investment Types
export const apiGetInvestmentTypes = () => {
  return apiRequest('/admin/investment-types');
};

export const apiAddInvestmentType = (data: any) => {
  return apiRequest('/admin/investment-types', {
    method: 'POST',
    body: JSON.stringify(data),
  });
};

export const apiUpdateInvestmentType = (id: number, data: any) => {
  return apiRequest(`/admin/investment-types/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
};

export const apiDeleteInvestmentType = (id: number) => {
  return apiRequest(`/admin/investment-types/${id}`, {
    method: 'DELETE',
  });
};

// Get total investment for a business unit
export const apiGetBusinessUnitInvestment = async (businessUnitId: number): Promise<number> => {
  try {
    const response = await apiRequest(`/investments/total?business_unit_id=${businessUnitId}`);
    return response.total_investment || 0;
  } catch (error) {
    console.error('Error fetching business unit investment:', error);
    return 0;
  }
};

// Get total expenses for a business unit
export const apiGetBusinessUnitExpenses = async (businessUnitId: number): Promise<number> => {
  try {
    const response = await apiRequest(`/expenses/total?business_unit_id=${businessUnitId}`);
    return response.total_expenses || 0;
  } catch (error) {
    console.error('Error fetching business unit expenses:', error);
    return 0;
  }
};

// Get comprehensive profit calculation data
export const apiGetProfitCalculation = async (businessUnitId: number): Promise<{
  inventory_value: number;
  total_investment: number;
  total_expenses: number;
  provisional_profit: number;
}> => {
  try {
    const response = await apiRequest(`/profit/calculation/${businessUnitId}`);
    return response;
  } catch (error) {
    console.error('Error fetching profit calculation:', error);
    throw error;
  }
};

// Capital API calls
export const apiGetCapital = (businessUnitId?: number) => {
  const params = businessUnitId ? `?business_unit_id=${businessUnitId}` : '';
  return apiRequest(`/capital${params}`);
};

export const apiUpdateCapital = (businessUnitId: number, amount: number, operation: 'add' | 'subtract') => {
  return apiRequest('/capital', {
    method: 'PUT',
    body: JSON.stringify({ business_unit_id: businessUnitId, amount, operation }),
  });
};

export const apiTransferCapital = (fromBusinessUnitId: number, toBusinessUnitId: number, amount: number) => {
  return apiRequest('/capital/transfer', {
    method: 'POST',
    body: JSON.stringify({ from_business_unit_id: fromBusinessUnitId, to_business_unit_id: toBusinessUnitId, amount }),
  });
};

export const apiGetPartners = async (): Promise<any[]> => {
  try {
    const response = await apiRequest('/partners');
    return response;
  } catch (error) {
    console.error('Get partners error:', error);
    throw error;
  }
};

// Add these new functions for capital transfer
export const apiGetAvailableInvestments = (business_unit_id?: number) => {
  const params = business_unit_id ? `?business_unit_id=${business_unit_id}` : '';
  return apiRequest(`/investments/available${params}`);
};

// REMOVED THE DUPLICATE FUNCTION - KEEPING THIS ONE:
export const apiTransferToCapital = (data: {
  investment_id: number;
  amount: number;
  description: string;
}) => {
  return apiRequest('/investments/transfer-to-capital', {
    method: 'POST',
    body: JSON.stringify(data),
  });
};

export const apiGetCapitalBalances = () => {
  return apiRequest('/capital/balances');
};

export const apiGetTransferHistory = (business_unit_id?: number) => {
  const params = business_unit_id ? `?business_unit_id=${business_unit_id}` : '';
  return apiRequest(`/capital/transfers/history${params}`);
};

// Add these functions to your existing apiService

export const apiGetUsers = async (): Promise<any> => {
  const response = await apiRequest('/admin/users');
  return response;
};

export const apiCreateUser = async (userData: any): Promise<any> => {
  const response = await apiRequest('/admin/users', {
    method: 'POST',
    body: JSON.stringify(userData),
  });
  return response;
};

export const apiUpdateUser = async (id: number, userData: any): Promise<any> => {
  const response = await apiRequest(`/admin/users/${id}`, {
    method: 'PUT',
    body: JSON.stringify(userData),
  });
  return response;
};

export const apiDeleteUser = async (id: number): Promise<any> => {
  const response = await apiRequest(`/admin/users/${id}`, {
    method: 'DELETE',
  });
  return response;
};

// Gold Price API
export const apiGetGoldPrice = () => {
  return apiRequest('/gold-price');
};

// Reports API
export const apiGetReports = (type: string, startDate?: string, endDate?: string) => {
  const params = new URLSearchParams();
  if (startDate) params.append('start_date', startDate);
  if (endDate) params.append('end_date', endDate);
  const queryString = params.toString();
  
  return apiRequest(`/reports/${type}${queryString ? `?${queryString}` : ''}`);
};


export default apiRequest;